export const S02_ASSET_TIERS = ['People', 'Data & Information', 'Physical Assets', 'Digital Systems'] as const;
export const S02_REGULATORY_FRAMEWORKS = ['ASIS PSC.1', 'GDPR', 'HIPAA', 'PCI-DSS', 'NIST CSF', 'ISO 27001', 'IEC 62443', 'Other / Client Policy'] as const;

export type S02Intake = {
    fapp_facility_name: string;
    fapp_address_address: string;
    fapp_address_city: string;
    fapp_address_state: string;
    fapp_address_zip: string;
    fapp_address_country: string;
    fapp_address_lat: number | null;
    fapp_address_lng: number | null;
    fapp_industry: string;
    fapp_facility_type: string;
    fapp_asset_tiers: string[];
    fapp_headcount: string;
    fapp_access_levels: string;
    fapp_existing_security: string;
    fapp_threat_concerns: string;
    fapp_regulatory_frameworks: string[];
    fapp_objectives_scope: string;
};

const text = (value: unknown, maximum = 4000) => String(value ?? '').trim().slice(0, maximum);
const coordinate = (value: unknown, minimum: number, maximum: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum ? parsed : null;
};
const values = (value: unknown, allowed: readonly string[]) => Array.isArray(value)
    ? [...new Set(value.map(item => text(item, 100)).filter(item => allowed.includes(item)))].slice(0, allowed.length)
    : [];

export function emptyS02Intake(): S02Intake {
    return {
        fapp_facility_name: '',
        fapp_address_address: '',
        fapp_address_city: '',
        fapp_address_state: '',
        fapp_address_zip: '',
        fapp_address_country: '',
        fapp_address_lat: null,
        fapp_address_lng: null,
        fapp_industry: '',
        fapp_facility_type: '',
        fapp_asset_tiers: [],
        fapp_headcount: '',
        fapp_access_levels: '',
        fapp_existing_security: '',
        fapp_threat_concerns: '',
        fapp_regulatory_frameworks: [],
        fapp_objectives_scope: '',
    };
}

export function cleanS02Intake(source: any): S02Intake {
    const input = source && typeof source === 'object' ? source : {};
    return {
        fapp_facility_name: text(input.fapp_facility_name || input.facilityName, 240),
        fapp_address_address: text(input.fapp_address_address || input.address, 500),
        fapp_address_city: text(input.fapp_address_city || input.city, 160),
        fapp_address_state: text(input.fapp_address_state || input.state, 160),
        fapp_address_zip: text(input.fapp_address_zip || input.postalCode, 40),
        fapp_address_country: text(input.fapp_address_country || input.country, 120),
        fapp_address_lat: coordinate(input.fapp_address_lat ?? input.lat, -90, 90),
        fapp_address_lng: coordinate(input.fapp_address_lng ?? input.lng, -180, 180),
        fapp_industry: text(input.fapp_industry || input.industry, 160),
        fapp_facility_type: text(input.fapp_facility_type || input.facilityType, 160),
        fapp_asset_tiers: values(input.fapp_asset_tiers || input.assetTiers, S02_ASSET_TIERS),
        fapp_headcount: text(input.fapp_headcount || input.headcount, 40),
        fapp_access_levels: text(input.fapp_access_levels || input.accessLevels, 1000),
        fapp_existing_security: text(input.fapp_existing_security || input.existingSecurity, 4000),
        fapp_threat_concerns: text(input.fapp_threat_concerns || input.threatConcerns, 4000),
        fapp_regulatory_frameworks: values(input.fapp_regulatory_frameworks || input.regulatoryFrameworks, S02_REGULATORY_FRAMEWORKS),
        fapp_objectives_scope: text(input.fapp_objectives_scope || input.objectivesScope, 4000),
    };
}

export function missingS02Intake(source: any): string[] {
    const intake = cleanS02Intake(source);
    const missing: string[] = [];
    if (!intake.fapp_facility_name)
        missing.push('Organisation / Facility Name');
    if (!intake.fapp_address_address || !intake.fapp_address_country)
        missing.push('Full Address and Country');
    if (!intake.fapp_industry)
        missing.push('Industry / Sector');
    if (!intake.fapp_facility_type)
        missing.push('Facility Type');
    if (!intake.fapp_asset_tiers.length)
        missing.push('Asset Tiers');
    if (!(Number(intake.fapp_headcount) > 0) || !intake.fapp_access_levels)
        missing.push('Headcount and Access Levels');
    if (!intake.fapp_existing_security)
        missing.push('Existing Security Measures');
    if (!intake.fapp_threat_concerns)
        missing.push('Known Threat Concerns');
    if (!intake.fapp_regulatory_frameworks.length)
        missing.push('Regulatory Frameworks');
    if (!intake.fapp_objectives_scope)
        missing.push('Plan Objectives and Scope');
    return missing;
}
